import type { CoasterProject } from '../schema/index.ts';

export interface EditorSelectionState {
  readonly nodeIds: readonly string[];
  readonly primaryNodeId: string | null;
  readonly sectionId: string | null;
  readonly sceneryIds: readonly string[];
  readonly primarySceneryId: string | null;
}

export interface EditorCommand {
  readonly id: string;
  readonly label: string;
  readonly before: CoasterProject;
  readonly after: CoasterProject;
  readonly selectionBefore: EditorSelectionState;
  readonly selectionAfter: EditorSelectionState;
  readonly payloadBytes: number;
  readonly mergeKey?: string;
}

export interface HistoryState {
  readonly canUndo: boolean;
  readonly canRedo: boolean;
  readonly undoCount: number;
  readonly redoCount: number;
  readonly payloadBytes: number;
}

const MAX_TRANSACTIONS = 100;
const MAX_PAYLOAD_BYTES = 8 * 1024 * 1024;

function estimateBytes(value: unknown): number {
  return new TextEncoder().encode(JSON.stringify(value)).byteLength;
}

function sameSelection(a: EditorSelectionState, b: EditorSelectionState): boolean {
  return (
    a.primaryNodeId === b.primaryNodeId &&
    a.sectionId === b.sectionId &&
    a.primarySceneryId === b.primarySceneryId &&
    a.nodeIds.length === b.nodeIds.length &&
    a.nodeIds.every((id, index) => id === b.nodeIds[index]) &&
    a.sceneryIds.length === b.sceneryIds.length &&
    a.sceneryIds.every((id, index) => id === b.sceneryIds[index])
  );
}

export function createProjectCommand(options: {
  readonly id: string;
  readonly label: string;
  readonly before: CoasterProject;
  readonly after: CoasterProject;
  readonly selectionBefore: EditorSelectionState;
  readonly selectionAfter: EditorSelectionState;
  readonly mergeKey?: string;
}): EditorCommand {
  const before = structuredClone(options.before);
  const after = structuredClone(options.after);
  return {
    ...options,
    before,
    after,
    selectionBefore: structuredClone(options.selectionBefore),
    selectionAfter: structuredClone(options.selectionAfter),
    payloadBytes: estimateBytes({ before, after }),
  };
}

export class CommandHistory {
  private readonly undoStack: EditorCommand[] = [];
  private readonly redoStack: EditorCommand[] = [];
  private bytes = 0;

  get state(): HistoryState {
    return {
      canUndo: this.undoStack.length > 0,
      canRedo: this.redoStack.length > 0,
      undoCount: this.undoStack.length,
      redoCount: this.redoStack.length,
      payloadBytes: this.bytes,
    };
  }

  push(command: EditorCommand): void {
    const previous = this.undoStack.at(-1);
    if (
      command.mergeKey &&
      previous?.mergeKey === command.mergeKey &&
      sameSelection(previous.selectionAfter, command.selectionBefore)
    ) {
      const merged = createProjectCommand({
        id: command.id,
        label: command.label,
        before: previous.before,
        after: command.after,
        selectionBefore: previous.selectionBefore,
        selectionAfter: command.selectionAfter,
        mergeKey: command.mergeKey,
      });
      this.undoStack[this.undoStack.length - 1] = merged;
      this.bytes += merged.payloadBytes - previous.payloadBytes;
    } else {
      this.undoStack.push(command);
      this.bytes += command.payloadBytes;
    }
    this.redoStack.length = 0;
    this.enforceBounds();
  }

  undo(): EditorCommand | null {
    const command = this.undoStack.pop();
    if (!command) return null;
    this.bytes -= command.payloadBytes;
    this.redoStack.push(command);
    return command;
  }

  redo(): EditorCommand | null {
    const command = this.redoStack.pop();
    if (!command) return null;
    this.undoStack.push(command);
    this.bytes += command.payloadBytes;
    this.enforceBounds();
    return command;
  }

  clear(): void {
    this.undoStack.length = 0;
    this.redoStack.length = 0;
    this.bytes = 0;
  }

  private enforceBounds(): void {
    while (this.undoStack.length > MAX_TRANSACTIONS || this.bytes > MAX_PAYLOAD_BYTES) {
      const evicted = this.undoStack.shift();
      if (!evicted) break;
      this.bytes -= evicted.payloadBytes;
    }
  }
}
